import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartItem, Product } from '../types';

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  total: number;
  addItem: (
    product: Product,
    quantity?: number,
    selectedAttrs?: {
      color?: string;
      size?: string;
      weight?: string;
      custom?: Record<string, string>;
      unit_price?: number;
    }
  ) => void;
  removeItem: (productId: number, itemIndex?: number) => void;
  updateQuantity: (productId: number, quantity: number, itemIndex?: number) => void;
  clearCart: () => void;
  setCart: (items: CartItem[]) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

const isSameCartItem = (
  a: { product: { id: number }; selected_color?: string; selected_size?: string; selected_weight?: string; selected_attributes?: Record<string, string> },
  b: { product: { id: number }; selected_color?: string; selected_size?: string; selected_weight?: string; selected_attributes?: Record<string, string> }
) => {
  if (a.product.id !== b.product.id) return false;
  if ((a.selected_color || '') !== (b.selected_color || '')) return false;
  if ((a.selected_size || '') !== (b.selected_size || '')) return false;
  if ((a.selected_weight || '') !== (b.selected_weight || '')) return false;

  const aAttrs = a.selected_attributes || {};
  const bAttrs = b.selected_attributes || {};
  const aKeys = Object.keys(aAttrs);
  const bKeys = Object.keys(bAttrs);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (aAttrs[k] !== bAttrs[k]) return false;
  }
  return true;
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Initialize state from localStorage
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error);
      return [];
    }
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);

  // Calculate total items
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  // Calculate total price considering variant unit_price
  const total = items.reduce((sum, item) => {
    const price = item.unit_price ?? item.product.final_price ?? item.product.price;
    return sum + price * item.quantity;
  }, 0);

  // Add item to cart
  const addItem = (
    product: Product,
    quantity: number = 1,
    selectedAttrs?: {
      color?: string;
      size?: string;
      weight?: string;
      custom?: Record<string, string>;
      unit_price?: number;
    }
  ) => {
    const candidateItem: CartItem = {
      product,
      quantity,
      unit_price: selectedAttrs?.unit_price ?? product.final_price ?? product.price,
      selected_color: selectedAttrs?.color || '',
      selected_size: selectedAttrs?.size || '',
      selected_weight: selectedAttrs?.weight || '',
      selected_attributes: selectedAttrs?.custom || {},
    };

    setItems((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => isSameCartItem(item, candidateItem));

      if (existingIndex > -1) {
        // Update quantity if identical variant already exists
        return prevItems.map((item, idx) =>
          idx === existingIndex
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add as a new distinct variant entry
        return [...prevItems, candidateItem];
      }
    });
  };

  // Remove item from cart (supports either specific index or by productId)
  const removeItem = (productId: number, itemIndex?: number) => {
    setItems((prevItems) => {
      if (itemIndex !== undefined && itemIndex >= 0 && itemIndex < prevItems.length) {
        return prevItems.filter((_, idx) => idx !== itemIndex);
      }
      return prevItems.filter((item) => item.product.id !== productId);
    });
  };

  // Update item quantity
  const updateQuantity = (productId: number, quantity: number, itemIndex?: number) => {
    if (quantity <= 0) {
      removeItem(productId, itemIndex);
      return;
    }

    setItems((prevItems) =>
      prevItems.map((item, idx) => {
        if (itemIndex !== undefined) {
          return idx === itemIndex ? { ...item, quantity } : item;
        }
        return item.product.id === productId ? { ...item, quantity } : item;
      })
    );
  };

  // Clear cart
  const clearCart = () => {
    setItems([]);
  };

  // Set entire cart (replace all items)
  const setCart = (newItems: CartItem[]) => {
    setItems(newItems);
  };

  const value: CartContextType = {
    items,
    itemCount,
    total,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
